import {DataFetcher, PriorityConfig} from '@aboutyou/data-fetcher';
import {ProductImageTypes} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_images.types';
import {ProductTilePreferences} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_tile_preferences.types';
import {ErrorPageInline, Slot} from '@aboutyou/ui-components';
import React, {FC, memo} from 'react';
import {UpdateNextToken} from '../TokenBasedStreamPages/UpdateNextToken';
import {NextPage} from '../TokenBasedStreamPages/useTokenBasedStreamPages';
import {ProductStreamSkeleton} from './ProductStreamSkeleton';
import {ProductStreamStructureMarkup} from './ProductStreamStructureMarkup';
import {StreamTileListProps} from './StreamTileList';
import {PRODUCTS_PER_PAGE} from './constants';

type TilesViewProps = StreamTileListProps &
  PriorityConfig & {
    page: NextPage<any>;
    preferredProductImageType?: ProductImageTypes;
    hideTopUSP?: boolean;
    enableImageGalleryAnimation?: boolean;
    errorComponent?: React.FC;
    loadingComponent?: React.FC;
    StreamTileList: React.ComponentType<any>;
    itemsPreferences?: ProductTilePreferences;
    isFetching?: boolean;
  } & Record<string, unknown>;

const Tiles = ({
  renderingPriority,
  errorComponent = ErrorComponent,
  loadingComponent = LoadingComponent,
  page,
  ...viewProps
}: TilesViewProps) => {
  const request = page.request({token: page.token});
  const isFirstPage = page.page === 1;
  const isFetching = request.isFetching;

  return (
    <>
      <UpdateNextToken request={request} page={page} />

      <DataFetcher
        request={request}
        Loading={loadingComponent}
        Error={errorComponent}
        viewProps={{
          ...viewProps,
          renderProductStructureMarkup: isFirstPage,
          isFetching,
        }}
        // Only the first page should be prioritized against other components on the first viewport.
        // On further pages we expect that other components are already loaded.
        // If every page is prioritized, this causes a re-rendering of the whole page which can harm performance.
        renderingPriority={isFirstPage ? renderingPriority : undefined}
        View={View}
      />
    </>
  );
};

const View = ({
  preferredProductImageType,
  StreamTileList,
  renderProductStructureMarkup,
  productTileGalleryMode = 'always',
  ...props
}: Omit<TilesViewProps, 'page'> & {
  StreamTileList: React.ComponentType<any>;
  items?: any[];
  renderProductStructureMarkup: boolean;
  productTileGalleryMode?: 'hover' | 'always';
}) => {
  const itemsPreferences =
    'itemsPreferences' in props
      ? (props.itemsPreferences as ProductTilePreferences)
      : undefined;

  const itemsPreferencesMerged = preferredProductImageType
    ? ({
        ...itemsPreferences,
        preferredProductImageType,
      } as ProductTilePreferences)
    : itemsPreferences;

  return (
    <>
      {renderProductStructureMarkup && props.items && (
        <ProductStreamStructureMarkup items={props.items} />
      )}
      <StreamTileList
        {...props}
        productTileGalleryMode={productTileGalleryMode}
        itemsPreferences={itemsPreferencesMerged!}
      />
    </>
  );
};

const LoadingComponent: FC = () => {
  // Loading 32 product tile makes sure we render close to the same amount of tiles that the backend will potentially return.
  // As a result, layout shifts if the user scrolls fast are reduced and then if the load more button is pressed we don't double fetch.
  // With the former approach of loading only 6 product tiles we always fetch 2 batches at once
  // as the loading component was already close to the viewport when the former component was still in loading state.
  return <ProductStreamSkeleton amount={PRODUCTS_PER_PAGE} />;
};

const ErrorComponent: FC = () => {
  return (
    <Slot position={1} columns={3}>
      <ErrorPageInline />
    </Slot>
  );
};

export const TilesByPage = memo(Tiles);
